import React, {useEffect, useRef, useState} from 'react'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Container,
    SimpleGrid,
    Skeleton,
    Stack,
    Text,
    Alert,
    AlertIcon,
    Button,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import ActionCard from '../../components/action-card/index'
import {AddressEditForm} from '../checkout/partials/address-form'
import PageActionPlaceHolder from '../../components/page-action-placeholder/index'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    useShopperBasketsMutation,
    useShopperCustomersMutation,
} from '@salesforce/commerce-sdk-react'
import {nanoid} from 'nanoid'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import AddressList from './addressLists'
import {useCheckoutActions} from '../../custom-hooks/use-checkout-actions'
import CustomToast from './customToast'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {remCalc} from '../../theme/_utils'

const DEFAULT_SKELETON_COUNT = 3

const ShippingAddressForm = ({
    form,
    toggleEdit,
    selectedAddressId,
    submitForm,
    isBillingAddress,
    addressType,
}) => {
    const styles = useMultiStyleConfig('Account')
    return (
        <Box borderRadius="base" position="relative" {...styles.formBox}>
            {form.formState.isSubmitting && <LoadingSpinner />}
            <Stack>
                <Box>
                    <Container variant="form" maxW="100%">
                        <form onSubmit={form.handleSubmit(submitForm)}>
                            <Stack spacing={6}>
                                {form.formState.errors?.global?.message && (
                                    <Alert status="error">
                                        <AlertIcon color="red.600" boxSize={4} />
                                        <Text fontSize="sm" ml={3}>
                                            {form.formState.errors.global.message}
                                        </Text>
                                    </Alert>
                                )}
                                <AddressEditForm
                                    form={form}
                                    onClose={toggleEdit}
                                    toggleAddressEdit={toggleEdit}
                                    submitForm={submitForm}
                                    selectedAddressId={selectedAddressId}
                                    isBillingAddress={isBillingAddress}
                                    addressType={addressType}
                                    isAccountEdit={true}
                                    variant="account-address"
                                />
                            </Stack>
                        </form>
                    </Container>
                </Box>
            </Stack>
        </Box>
    )
}

ShippingAddressForm.propTypes = {
    form: PropTypes.object,
    hasAddresses: PropTypes.bool,
    selectedAddressId: PropTypes.string,
    toggleEdit: PropTypes.func,
    submitForm: PropTypes.func,
    isBillingAddress: PropTypes.bool,
    addressType: PropTypes.string,
}

const successfullyAddedAddress = defineMessage({
    defaultMessage: 'New address saved',
    id: 'account_addresses.info.new_address_saved',
})

const successfullyUpdatedAddress = defineMessage({
    defaultMessage: 'Address updated',
    id: 'account_addresses.info.address_updated',
})

const successfullyRemovedAddress = defineMessage({
    defaultMessage: 'Address removed',
    id: 'account_addresses.info.address_removed',
})

const AccountAddresses = () => {
    const {formatMessage} = useIntl()
    const {data: customer, isLoading, refetch} = useCurrentCustomer()
    const {isRegistered, addresses, customerId} = customer

    const addCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateSavedAddress = useShopperCustomersMutation('updateCustomerAddress')
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')
    const [customToast, setCustomToast] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [selectedAddressId, setSelectedAddressId] = useState(false)
    const [isSettingDefault, setIsSettingDefault] = useState(false) // Stato per il caricamento
    const form = useForm({
        mode: 'all',
    })
    const styles = useMultiStyleConfig('Account')
    const headingRef = useRef()
    const {createBody} = useCheckoutActions()

    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment',
    )
    const {data: basket} = useCurrentBasket()

    useEffect(() => {
        // Focus the 'Addresses' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    // keep track of the edit buttons so we can focus on them later for accessibility
    const [editBtnRefs, setEditBtnRefs] = useState({})
    useEffect(() => {
        const currentRefs = {}
        addresses?.forEach(({addressId}) => {
            currentRefs[addressId] = React.createRef()
        })
        setEditBtnRefs(currentRefs)
    }, [addresses])

    const showError = () => {
        setCustomToast({
            message: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
        })
    }

    const submitForm = async (address) => {
        try {
            let data
            form.clearErrors()
            if (selectedAddressId) {
                const {addressId, body} = createBody(address, !!address?.c_vatNumber)
                data = await updateSavedAddress.mutateAsync({
                    body: {...body, addressId},
                    parameters: {
                        customerId,
                        addressName: selectedAddressId,
                    },
                })
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                const {addressId, body} = createBody(address, false)
                data = await addCustomerAddress.mutateAsync({
                    body: {
                        ...body,
                        addressId: nanoid(),
                    },
                    parameters: {customerId: customer.customerId},
                })
            }
            if (data) {
                toggleEdit()
                setCustomToast({
                    message: selectedAddressId
                        ? formatMessage(successfullyUpdatedAddress)
                        : formatMessage(successfullyAddedAddress),
                    status: 'success',
                    variant: 'success',
                    isClosable: true,
                })
            }

            refetch()
        } catch (error) {
            setCustomToast({
                message: error.message,
                status: 'error',
                variant: 'error',
            })
            form.setError('global', {type: 'manual', message: error.message})
            setIsEditing(false)
        }
    }

    const removeAddress = async (addressId) => {
        try {
            if (addressId === selectedAddressId) {
                setSelectedAddressId(undefined)
                setIsEditing(false)
                form.reset({addressId: ''})
            }
            await removeCustomerAddress.mutateAsync(
                {
                    parameters: {
                        customerId,
                        addressName: addressId,
                    },
                },
                {
                    onSuccess: async () => {
                        if (
                            basket?.shipments[0]?.shippingAddress?.c_addressId === addressId ||
                            !basket?.shipments[0]?.shippingAddress?.c_addressId
                        ) {
                            await updateShippingAddressForShipment.mutateAsync({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: 'me',
                                    useAsBilling: false,
                                },
                                body: {},
                            })
                        }

                        setCustomToast({
                            message: formatMessage(successfullyRemovedAddress),
                            status: 'success',
                            variant: 'success',
                        })
                        // Move focus to header after we successfully remove address
                        headingRef?.current?.focus()
                    },
                },
            )
        } catch (error) {
            showError()
            throw error
        }
    }

    const toggleEdit = (address) => {
        form.reset({...address})

        if (address?.addressId) {
            setSelectedAddressId(address.addressId)
            setIsEditing(true)
        } else {
            setSelectedAddressId(undefined)
            setIsEditing(!isEditing)
        }
    }

    const setDefaultAddress = async (addressId) => {
        try {
            setIsSettingDefault(true)
            const address = addresses.find((addr) => addr.addressId === addressId)
            if (address) {
                // Determine if the address is billing or shipping
                const isBillingAddress = address.c_isB2BAddress || address.c_isB2CBillingAddress

                // Update the previous default address to remove its default status
                const previousDefault = addresses.find(
                    (addr) =>
                        addr.c_isPreferredAddress &&
                        (isBillingAddress
                            ? addr.c_isB2BAddress || addr.c_isB2CBillingAddress
                            : !addr.c_isB2BAddress && !addr.c_isB2CBillingAddress),
                )
                if (previousDefault) {
                    const {addressId: prevAddressId, body: prevBody} = createBody(
                        previousDefault,
                        previousDefault.c_isB2CBillingAddress || previousDefault.c_isB2BAddress,
                        false,
                    )
                    await updateSavedAddress.mutateAsync({
                        body: {...prevBody, addressId: prevAddressId},
                        parameters: {
                            customerId,
                            addressName: prevAddressId,
                        },
                    })
                }

                // Set the new default address
                const {addressId: newAddressId, body: newBody} = createBody(
                    address,
                    address.c_isB2CBillingAddress || address.c_isB2BAddress,
                    true,
                )
                await updateSavedAddress.mutateAsync({
                    body: {...newBody, addressId: newAddressId},
                    parameters: {
                        customerId,
                        addressName: newAddressId,
                    },
                })

                setCustomToast({
                    message: formatMessage(successfullyUpdatedAddress),
                    status: 'success',
                    variant: 'success',
                    isClosable: true,
                })
                refetch()
            }
        } catch (error) {
            setCustomToast({
                message: error.message,
                status: 'error',
                variant: 'error',
            })
        } finally {
            setIsSettingDefault(false)
        }
    }

    const shippingAddressess =
        customer.addresses?.filter((address) => {
            return !address?.c_isB2CBillingAddress && !address?.c_isB2BAddress
        }) || []
    const billingBusinessAddresses =
        customer.addresses?.filter((address) => address?.c_isB2BAddress) || []
    const billingPrivateAddresses =
        customer.addresses?.filter((address) => address?.c_isB2CBillingAddress) || []

    const billingAddresses = [...billingBusinessAddresses, ...billingPrivateAddresses]

    const hasAddresses = shippingAddressess?.length > 0

    const hasBillings = billingAddresses?.length > 0

    return (
        <Stack data-testid="account-addresses-page" gap={{base: remCalc(16), lg: remCalc(24)}}>
            <Text
                as="h1"
                {...styles.accountTitle}
                tabIndex="0"
                ref={headingRef}
                display={isEditing ? 'none' : 'block'}
            >
                <FormattedMessage
                    defaultMessage="Addresses"
                    id="account_addresses.title.addresses"
                />
            </Text>
            {isSettingDefault && <LoadingSpinner size="sm" />}
            {isLoading && (
                <Stack spacing={[6]}>
                    <SimpleGrid columns={[1, 2, 2, 2]} spacing={4}>
                        {new Array(DEFAULT_SKELETON_COUNT).fill().map((_, index) => {
                            return (
                                <ActionCard key={index}>
                                    <Stack spacing={2} marginBottom={7}>
                                        <Skeleton height="23px" width="120px" />

                                        <Skeleton height="23px" width="84px" />

                                        <Skeleton height="23px" width="104px" />
                                    </Stack>
                                </ActionCard>
                            )
                        })}
                    </SimpleGrid>
                </Stack>
            )}

            {customToast && (
                <CustomToast
                    message={customToast.message}
                    status={customToast.status}
                    onClose={() => setCustomToast(null)}
                    duration={5000}
                />
            )}

            {hasAddresses && (
                <>
                    <AddressList
                        addresses={shippingAddressess}
                        isEditing={isEditing}
                        selectedAddressId={selectedAddressId}
                        toggleEdit={toggleEdit}
                        removeAddress={removeAddress}
                        form={form}
                        submitForm={submitForm}
                        editBtnRefs={editBtnRefs}
                        hasAddresses={hasAddresses}
                        title="Shipping Address"
                        headingRef={headingRef}
                        ShippingAddressForm={ShippingAddressForm}
                        onSetDefault={setDefaultAddress}
                        isBillingAddress={false}
                    />

                    <Button
                        variant="secondary"
                        {...styles.confirmDeleteButton}
                        onClick={() => toggleEdit()}
                        display={isEditing ? 'none' : 'inline-flex'}
                    >
                        add address
                    </Button>

                    {isEditing && !selectedAddressId && (
                        <ShippingAddressForm
                            form={form}
                            hasAddresses={hasAddresses}
                            submitForm={submitForm}
                            selectedAddressId={selectedAddressId}
                            toggleEdit={toggleEdit}
                        />
                    )}
                </>
            )}

            {hasBillings && (
                <AddressList
                    addresses={billingAddresses}
                    isEditing={isEditing}
                    selectedAddressId={selectedAddressId}
                    toggleEdit={toggleEdit}
                    removeAddress={removeAddress}
                    form={form}
                    submitForm={submitForm}
                    editBtnRefs={editBtnRefs}
                    hasAddresses={hasBillings}
                    title="Billing Address"
                    headingRef={headingRef}
                    ShippingAddressForm={ShippingAddressForm}
                    onSetDefault={setDefaultAddress}
                    isBillingAddress={true}
                />
            )}

            {!hasAddresses && !isLoading && (
                <Stack spacing={6}>
                    {!isEditing && isRegistered && (
                        <>
                            <PageActionPlaceHolder
                                variant="account-address"
                                heading={formatMessage({
                                    defaultMessage: 'Shipping Address',
                                    id: 'account_addresses.page_action_placeholder.heading.saved_addresses',
                                })}
                                buttonText={formatMessage({
                                    defaultMessage: 'add address',
                                    id: 'account_addresses.page_action_placeholder.button.add_address',
                                })}
                                onButtonClick={() => toggleEdit()}
                            />
                            <PageActionPlaceHolder
                                variant="account-billing"
                                heading={formatMessage({
                                    defaultMessage: 'Billing Address',
                                    id: 'account_addresses.page_action_placeholder.heading.saved_billing',
                                })}
                                buttonText={formatMessage({
                                    defaultMessage: 'Add Billing Address',
                                    id: 'account_addresses.page_action_placeholder.button.add_billing',
                                })}
                                onButtonClick={() => toggleEdit()}
                            />
                        </>
                    )}
                    {isEditing && !selectedAddressId && (
                        <ShippingAddressForm
                            form={form}
                            hasAddresses={hasAddresses}
                            submitForm={submitForm}
                            selectedAddressId={selectedAddressId}
                            toggleEdit={toggleEdit}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    )
}

AccountAddresses.getTemplateName = () => 'account-addresses'

export default AccountAddresses
